// =============================================================================
// features/simulator/firstDueDate.ts — Helpers de calendário para o campo
// "Data da primeira parcela" (F44-S06).
//
// IMPORTANTE — isto NÃO é cálculo financeiro. É só aritmética de calendário
// (dia/mês/ano) para posicionar o valor default e o `min`/`max` do seletor de
// data. Toda conta que envolve juros, carência ou parcela é feita e validada
// no backend (`apps/api/src/modules/simulations/grace.ts`) — o front nunca
// recalcula esses números, só espelha os limites já publicados por esse
// módulo (`MIN_GRACE_DAYS`/`MAX_GRACE_DAYS`) para dar feedback imediato no
// seletor. O backend segue sendo a fonte de verdade: qualquer data fora da
// janela ainda é rejeitada por lá (`FIRST_DUE_DATE_TOO_SOON`/`_TOO_LATE`).
//
// Datas como componentes Y/M/D locais, nunca math de epoch/UTC — mesma
// convenção do grace.ts, evita o descompasso de fuso já visto em outros
// módulos (computeRange).
// =============================================================================

/** Espelha grace.ts — carência mínima até a primeira parcela. */
export const MIN_GRACE_DAYS = 30;

/** Espelha grace.ts — teto de carência informado pelo BdP em 29/08/2026. */
export const MAX_GRACE_DAYS = 90;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Data de hoje (fuso local do navegador — é o operador quem preenche) em YYYY-MM-DD. */
export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Soma `days` dias de calendário a uma data YYYY-MM-DD, em componentes locais. */
export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

/** Default do seletor: hoje + MIN_GRACE_DAYS — mesma condição padrão do backend. */
export function defaultFirstDueDate(): string {
  return addDaysISO(todayISO(), MIN_GRACE_DAYS);
}

/** Mínimo aceito pelo seletor — idêntico ao default (carência mínima). */
export function minFirstDueDate(): string {
  return defaultFirstDueDate();
}

/** Teto exibido no seletor — apenas UX; o backend segue validando de verdade. */
export function maxFirstDueDate(): string {
  return addDaysISO(todayISO(), MAX_GRACE_DAYS);
}
